import { useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { LocaleLink } from "@/components/site/LocaleLink";
import { Button } from "@/components/ui/button";
import { trackFormFailure, trackFormSubmit } from "@/lib/analytics/track";
import { failureReasonForStatus } from "@/lib/analytics/cta-events";
import { logFormSubmission, hubspotFailReason } from "@/lib/form-log/client";

/** Newsletter signup posting to the existing HubSpot "new reports" forms (EN/BG). Used on /reports and every report page. */
export function ReportsNewsletter({
  isBg,
  variant = "card",
  reportCount,
}: {
  isBg: boolean;
  variant?: "card" | "hero" | "grid";
  reportCount?: number;
}) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email || !/.+@.+\..+/.test(email)) {
      setError(isBg ? "Въведете валиден имейл" : "Enter a valid email");
      return;
    }
    if (!consent) {
      setError(isBg ? "Необходимо е съгласие" : "Consent is required");
      return;
    }
    setSubmitting(true);
    const backupBase = {
      formType: "reports-notify",
      formName: "reports-notify-me",
      pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      email,
      payload: { locale: isBg ? "bg" : "en", placement: variant === "hero" ? "reports_hero" : variant === "grid" ? "reports_grid" : "card" },
    };
    let hsStatus: number | null = null;
    try {
      const formId = isBg
        ? "930ef5bf-9ed4-4625-a4b1-8aadb2027265"
        : "e12d3da2-3c21-4096-99cd-64a4cc18a27f";
      const res = await fetch(
        `https://api-eu1.hsforms.com/submissions/v3/integration/submit/25788344/${formId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [{ objectTypeId: "0-1", name: "email", value: email }],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : "",
            },
            legalConsentOptions: {
              consent: {
                consentToProcess: true,
                text: isBg
                  ? "Съгласен съм с Политиката за поверителност и получаване на имейли за нови доклади."
                  : "I agree to the Privacy Policy and to receiving emails when new reports are published.",
              },
            },
          }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as { inlineMessage?: string; message?: string };
      if (!res.ok) {
        hsStatus = res.status;
        trackFormFailure({ form_name: "reports-notify-me", reason: failureReasonForStatus(res.status), error_code: res.status, locale: isBg ? "bg" : "en" });
        throw new Error(data.message ?? "failed");
      }
      setSubmitted(true);
      void logFormSubmission({ ...backupBase, hubspotOk: true });
      toast.success(isBg ? "Абонаментът е активен - благодарим!" : "You're subscribed - thanks!");
      trackFormSubmit("submit_reports_notify", { form_name: "reports-notify-me", locale: isBg ? "bg" : "en", placement: variant === "hero" ? "reports_hero" : variant === "grid" ? "reports_grid" : "card" });
    } catch (err) {
      void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(hsStatus ?? null) });
      const msg =
        hsStatus !== null && err instanceof Error && err.message && err.message !== "failed"
          ? err.message
          : isBg
            ? "Нещо се обърка. Опитайте отново."
            : "Something went wrong. Try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (variant === "grid") {
    return (
      <div className="relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-accent/30 bg-[image:var(--gradient-brand-soft)] p-6 shadow-[var(--shadow-soft)]">
        <div>
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Mail className="h-5 w-5" aria-hidden />
          </span>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-strong">
            {isBg ? "Ранен достъп" : "Insider access"}
          </p>
          <h2 className="font-display mt-2 text-[17px] font-medium leading-snug text-foreground">
            {isBg ? "Получавайте следващия доклад преди всички." : "Get the next report before anyone else."}
          </h2>
          <p className="mt-2 text-[13px] leading-relaxed text-foreground/75">
            {isBg ? "Един имейл за доклад. Без спам." : "One email per report. No spam."}
          </p>
        </div>
        {submitted ? (
          <p className="mt-5 rounded-xl border border-accent/30 bg-background p-4 text-[13px] text-foreground">
            {isBg ? "Записахте се. Следващият доклад ще бъде първо във вашата поща." : "You're on the list. The next report lands in your inbox first."}
          </p>
        ) : (
          <form name="reports-newsletter-grid" data-hs-do-not-collect="true" onSubmit={handleSubmit} className="mt-5 space-y-2.5">
            <label htmlFor="reports-grid-email" className="sr-only">{isBg ? "Имейл" : "Email"}</label>
            <input
              id="reports-grid-email"
              type="email"
              autoComplete="email"
              required
              placeholder={isBg ? "вашият@имейл.com" : "you@company.com"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border/60 bg-background px-3 py-2.5 text-[13.5px] text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <label className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-accent" />
              <span>
                {isBg ? "Съгласен съм с " : "I agree to the "}
                <LocaleLink to="/privacy-policy" className="font-semibold text-accent-strong hover:underline">
                  {isBg ? "Политиката за поверителност" : "Privacy Policy"}
                </LocaleLink>
                .
              </span>
            </label>
            <Button type="submit" disabled={submitting} className="w-full bg-gradient-brand shadow-glow transition-transform hover:-translate-y-0.5">
              {submitting ? (isBg ? "Изпраща…" : "Sending…") : isBg ? "Искам ранен достъп" : "Get early access"}
            </Button>
            {error && <p className="text-[12px] text-destructive">{error}</p>}
          </form>
        )}
      </div>
    );
  }

  if (variant === "hero") {
    return (
      <section
        aria-labelledby="reports-early-access"
        className="relative mt-10 overflow-hidden rounded-3xl border border-accent/25 bg-[image:var(--gradient-brand-soft)] p-7 shadow-[var(--shadow-soft)] lg:p-10"
      >
        <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-10">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-strong">
              {isBg ? "Ранен достъп" : "Insider access"}
            </p>
            <h2 id="reports-early-access" className="font-display mt-3 text-[clamp(1.4rem,2.4vw,1.9rem)] font-medium leading-tight text-foreground">
              {isBg ? "Четете нашите изследвания преди всички останали." : "Read our research before anyone else."}
            </h2>
            <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-foreground/75">
              {isBg
                ? "Абонатите получават всеки нов доклад на Perceptica в деня на публикуването, преди медиите и социалните мрежи. Един имейл за доклад. Без спам."
                : "Subscribers get every new Perceptica report in their inbox on publication day, ahead of press coverage and social posts. One email per report. No spam."}
            </p>
            {reportCount ? (
              <p className="mt-3 text-[12.5px] font-medium text-foreground/70">
                {isBg ? `${reportCount} публикувани доклада досега` : `${reportCount} reports published so far`}
              </p>
            ) : null}
          </div>
          {submitted ? (
            <p className="rounded-xl border border-accent/30 bg-background p-5 text-[14px] text-foreground">
              {isBg
                ? "Записахте се. Следващият доклад ще бъде първо във вашата поща."
                : "You're on the list. The next report lands in your inbox first."}
            </p>
          ) : (
            <form name="reports-newsletter" id="reports-newsletter" data-hs-do-not-collect="true" onSubmit={handleSubmit} className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <label htmlFor="reports-hero-email" className="sr-only">{isBg ? "Имейл" : "Email"}</label>
                <input
                  id="reports-hero-email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder={isBg ? "вашият@имейл.com" : "you@company.com"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-border/60 bg-background px-3 py-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                />
                <Button type="submit" size="lg" disabled={submitting} className="bg-gradient-brand shadow-glow transition-transform hover:-translate-y-0.5 sm:flex-none">
                  {submitting ? (isBg ? "Изпраща…" : "Sending…") : isBg ? "Искам ранен достъп" : "Get early access"}
                </Button>
              </div>
              <label className="flex items-start gap-2 text-[12px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-accent"
                />
                <span>
                  {isBg ? "Съгласен съм с " : "I agree to the "}
                  <LocaleLink to="/privacy-policy" className="font-semibold text-accent-strong hover:underline">
                    {isBg ? "Политиката за поверителност" : "Privacy Policy"}
                  </LocaleLink>
                  .
                </span>
              </label>
              {error && <p className="text-[12px] text-destructive">{error}</p>}
            </form>
          )}
        </div>
      </section>
    );
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-background p-8 shadow-[var(--shadow-soft)] lg:p-10">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] bg-accent/10 text-accent">
        <Mail className="h-5 w-5" />
      </span>
      <h2 className="font-display mt-5 text-[1.35rem] font-medium text-foreground">
        {isBg ? "Получавайте новите доклади първи" : "Get new reports first"}
      </h2>
      <p className="mt-3 max-w-md text-[14px] leading-relaxed text-foreground/75">
        {isBg
          ? "Кратък имейл всеки път, когато публикуваме нов доклад. Без спам."
          : "One email when we publish a new report. No spam, no marketing sequences."}
      </p>
      {submitted ? (
        <p className="mt-6 rounded-lg border border-accent/30 bg-accent/5 p-4 text-[13.5px] text-foreground">
          {isBg
            ? "Готово - ще ви пишем при следващия доклад."
            : "You're in - we'll email you when the next report drops."}
        </p>
      ) : (
        <form name="reports-newsletter" id="reports-newsletter" data-hs-do-not-collect="true" onSubmit={handleSubmit} className="mt-6 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="email"
              required
              placeholder={isBg ? "вашият@имейл.com" : "you@company.com"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border/60 bg-background px-3 py-2.5 text-[13.5px] text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <Button type="submit" disabled={submitting} className="sm:flex-none">
              {submitting ? (isBg ? "Изпраща…" : "Sending…") : isBg ? "Абонирай ме" : "Notify me"}
            </Button>
          </div>
          <label className="flex items-start gap-2 text-[12px] text-muted-foreground">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-accent"
            />
            <span>
              {isBg ? "Съгласен съм с " : "I agree to the "}
              <LocaleLink to="/privacy-policy" className="font-semibold text-accent hover:underline">
                {isBg ? "Политиката за поверителност" : "Privacy Policy"}
              </LocaleLink>
              .
            </span>
          </label>
          {error && <p className="text-[12px] text-destructive">{error}</p>}
        </form>
      )}
    </div>
  );
}

